<template>
    <el-main class="project-timeline rel f-col h-full hidden-y p-0">
        <!-- Header -->
        <div class="project-timeline__header _px-1 _mt-1">
            <el-button v-if="dispalyedEvents.length"
                       class="font-medium"
                       type="text"
                       icon="el-icon-back"
                       @click="goBack">
                Вернуться назад
            </el-button>
        </div>
        <!-- Timeline -->
        <div ref="timeline"
             class="h-full overflow-auto with-custom-scrollbar-dark">
            <div v-for="event in timeline"
                 :key="event.key"
                 class="border-bottom-1 border-gray-100 space-y-1 _p-1">
                <our-label :value="event.key"
                           class="font-medium"
                           primary />

                <el-timeline class="p-0">
                    <!-- Acceptance of work -->
                    <access permissions="project_tasks_acceptance_works_show"
                            hidable>
                        <project-timeline-item v-if="event.acceptance_of_work"
                                               icon="acceptance"
                                               :value="event.acceptance_of_work"
                                               :type="projectTimelineTypes.ACCEPTANCE_OF_WORK"
                                               :displayed-value-count="displayedValueCount"
                                               with-members
                                               @show-all="showAll({ key: event.key, date: event.date, type: projectTimelineTypes.ACCEPTANCE_OF_WORK })" />
                    </access>

                    <!-- Defects and violations -->
                    <access permissions="project_tasks_defects_show"
                            hidable>
                        <project-timeline-item v-if="event.defects_and_violations"
                                               icon="defect"
                                               :value="event.defects_and_violations"
                                               :type="projectTimelineTypes.DEFECTS_AND_VIOLATIONS"
                                               :displayed-value-count="displayedValueCount"
                                               with-members
                                               with-address
                                               @show-all="showAll({ key: event.key, date: event.date, type: projectTimelineTypes.DEFECTS_AND_VIOLATIONS })" />
                    </access>

                    <!-- Defects and violations (Finished) -->
                    <access permissions="project_tasks_defects_show"
                            hidable>
                        <project-timeline-item v-if="event.defects_and_violations_finished"
                                               icon="defect"
                                               :value="event.defects_and_violations_finished"
                                               :type="projectTimelineTypes.DEFECTS_AND_VIOLATIONS_FINISHED"
                                               :displayed-value-count="displayedValueCount"
                                               with-members
                                               with-address
                                               @show-all="showAll({ key: event.key, date: event.date, type: projectTimelineTypes.DEFECTS_AND_VIOLATIONS_FINISHED })" />
                    </access>

                    <!-- Approval process -->
                    <access :permissions="permissionsAny(
                                'project_approval_process_be_chief_project_engineer',
                                'project_approval_process_be_document_approver',
                                'project_approval_process_be_project_designer'
                            )"
                            hidable>
                        <template v-if="event.approval_process">
                            <project-timeline-item v-for="processes in event.approval_process"
                                                   :key="processes.key"
                                                   icon="approval"
                                                   :value="processes"
                                                   :type="projectTimelineTypes.APPROVAL_PROCESS"
                                                   :displayed-value-count="displayedValueCount"
                                                   with-initiator
                                                   @show-all="showAll({ key: event.key, date: event.date, type: projectTimelineTypes.APPROVAL_PROCESS })" />
                        </template>
                    </access>

                    <!-- Approval process (Approved) -->
                    <access :permissions="permissionsAny(
                                'project_approval_process_be_chief_project_engineer',
                                'project_approval_process_be_document_approver',
                                'project_approval_process_be_project_designer'
                            )"
                            hidable>
                        <template v-if="event.approval_process_approved">
                            <project-timeline-item v-for="processes in event.approval_process_approved"
                                                   :key="processes.key"
                                                   icon="approval"
                                                   :value="processes"
                                                   :type="projectTimelineTypes.APPROVAL_PROCESS_APPROVED"
                                                   :displayed-value-count="displayedValueCount"
                                                   with-initiator
                                                   @show-all="showAll({ key: event.key, date: event.date, type: projectTimelineTypes.APPROVAL_PROCESS_APPROVED })" />
                        </template>
                    </access>

                    <!-- Documents -->
                    <access :permissions="permissionsAny(
                                'project_documents_work_documentation_show',
                                'project_documents_bim_model_show',
                                'project_documents_project_work_production_show',
                                'project_documents_executive_documentation_show',
                                'project_documents_initial_permitting_documentation_show',
                                'project_documents_project_documentation_show',
                                'project_documents_other_documentation_show'
                            )"
                            hidable>
                        <project-timeline-item v-if="event.documents"
                                               icon="document"
                                               :value="event.documents"
                                               :type="projectTimelineTypes.DOCUMENTS"
                                               :displayed-value-count="displayedValueCount"
                                               @show-all="showAll({ date: event.date, type: projectTimelineTypes.DOCUMENTS })" />
                    </access>

                    <!-- Photos -->
                    <project-timeline-item v-if="event.photos"
                                           el-icon="el-icon-camera-solid"
                                           :value="event.photos"
                                           :type="projectTimelineTypes.PHOTOS"
                                           :displayed-value-count="displayedValueCount"
                                           @show-all="showAll({ key: event.key, date: event.date, type: projectTimelineTypes.PHOTOS })" />
                </el-timeline>
            </div>
        </div>

        <el-button v-if="canLoadMore && !dispalyedEvents.length"
                   class="abs-x-center b-1 depth-10"
                   type="primary"
                   round
                   :icon="loading ? 'el-icon-loading' : ''"
                   @click="loadMore">
            <span v-if="!loading">Показать еще</span>
        </el-button>
    </el-main>
</template>

<script>
import { mapActions } from 'vuex'
import { endOfDay, startOfDay, subWeeks } from 'date-fns'

import { actionable } from '@/store/connectors'

import { labelAt, format } from '@/utils/date'
import { sortAsDate } from '@/utils/immutable'

import { projectTimelineTypes } from '@/models/projectTimeline'
import { types } from '@/models/tasks'
import { treeTypes } from '@/models/documents'

import ProjectTimelineItem from '@/components/projects/ProjectTimelineItem'

export default {
  name: 'ProjectTimeline',

  components: {
    ProjectTimelineItem
  },

  mixins: [
    actionable({ on: 'tasks', name: 'fetchTasksSliced' }),
    actionable({ on: 'documents', name: 'fetchDocumentsSliced' }),
    actionable({ on: 'photos', name: 'fetchPhotosSliced' })
  ],

  data() {
    const now = new Date()

    return {
      loading: false,

      from: startOfDay(now),
      to: endOfDay(now),

      photoType: 'projectPhotos',

      data: [],
      meta: {},

      scrollTop: 0,
      dispalyedEvents: [],

      permissionsAny,
      projectTimelineTypes
    }
  },

  computed: {
    timeline() {
      const res = (this.dispalyedEvents.length && this.dispalyedEvents) || Object.values(
        [ ...this.data ]
          .sort(sortAsDate((x) => new Date(this.getDate(x)), 'desc'))
          .reduce((r, x) => {
            const date = this.getDate(x)
            const key = this.prepareDate(date)

            return ({
              ...r,
              [key]: {
                ...r[key],
                key,
                
                date: format(new Date(date), { withTime: false }),

                [x._type]: x.type === types.APPROVAL_PROCESS
                  ? {
                    ...r[key]?.[x._type],

                    [x.data.main_document?.type]: {
                      key: key + x._type + x.data.main_document?.type,
                      type: x.data.main_document?.type,
                      translated_type: x.data.main_document?.translated_type || 'Неизвестно',
                      value: [ ...r?.[key]?.[x._type]?.[x.data.main_document?.type]?.value || [], x ]
                    }
                  }  
                  : [ ...r?.[key]?.[x._type] || [], x ]
              }
            })
          }, {})
      )
      
      return res.map(x => ({
        ...x,
        ...x[projectTimelineTypes.APPROVAL_PROCESS] && { [projectTimelineTypes.APPROVAL_PROCESS]: Object.values(x[projectTimelineTypes.APPROVAL_PROCESS]) },
        ...x[projectTimelineTypes.APPROVAL_PROCESS_APPROVED] && { [projectTimelineTypes.APPROVAL_PROCESS_APPROVED]: Object.values(x[projectTimelineTypes.APPROVAL_PROCESS_APPROVED]) }
      }))
    },

    canLoadMore() {
      return Object.values(this.meta).filter(x => x.next_at).length
    },

    displayedValueCount() {
      return this.dispalyedEvents.length ? 0 : 3
    }
  },

  mounted() {
    this.init()
      .then(data => {
        const eventsCount = data.reduce((count, x) => count + x.data.length, 0)
        
        if (!eventsCount) this.loadMore()
      })
  },

  methods: {
    ...mapActions({
      loadPhotos: 'photos/loadPhotos'
    }),

    init() {
      this.loading = true

      const requestData = [
        { timelineType: projectTimelineTypes.ACCEPTANCE_OF_WORK, filterByDateType: 'created', fetch: this.fetchTasksSliced },
        { timelineType: projectTimelineTypes.DEFECTS_AND_VIOLATIONS, filterByDateType: 'created', fetch: this.fetchTasksSliced },
        { timelineType: projectTimelineTypes.DEFECTS_AND_VIOLATIONS_FINISHED, type: projectTimelineTypes.DEFECTS_AND_VIOLATIONS, filterByDateType: 'finished', fetch: this.fetchTasksSliced },
        { timelineType: projectTimelineTypes.APPROVAL_PROCESS, filterByDateType: 'created', fetch: this.fetchTasksSliced },
        { timelineType: projectTimelineTypes.APPROVAL_PROCESS_APPROVED, type: projectTimelineTypes.APPROVAL_PROCESS, filterByDateType: 'approved', fetch: this.fetchTasksSliced },
        { timelineType: projectTimelineTypes.DOCUMENTS, filterByDateType: 'created', fetch: this.fetchDocumentsSliced },
        { timelineType: projectTimelineTypes.PHOTOS, filterByDateType: 'created', fetch: this.fetchPhotosSliced }
      ]

      return Promise.all(
        requestData
          .filter(({ timelineType }) => !this.meta[timelineType] || this.meta[timelineType]?.next_at)
          .map(({ timelineType, type, filterByDateType, fetch }) => 
            fetch(this.createRequestParams(type || timelineType, filterByDateType))
              .then(({ data }) => ({
                timelineType,
                data: data.data.data.map(x => ({ ...x, _type: timelineType })),
                meta: data.data.meta
              }))
          ) 
      ).then(data => {
        this.data = [ ...this.data, ...data.flatMap(x => x.data) ]
        this.meta = data.reduce((r, x) => ({ ...r, [x.timelineType]: x.meta }), {})

        return data
      }).finally(() => {
        this.loading = false
      })
    },

    createRequestParams(type, filterByDateType) {
      return ({
        projectId: this.$route.params.projectId,
        from: this.from,
        to: this.to,
        sort: ({
          created: '-created_at',
          finished: '-finished_at',
          approved: '-approved_at'
        })[filterByDateType],

        ...(type !== projectTimelineTypes.DOCUMENTS || type !== projectTimelineTypes.PHOTOS) && {
          type,
          filterByDateType,
          withProject: true,
          withMembers: true
        },
        
        ...type === projectTimelineTypes.APPROVAL_PROCESS && { withMainDocument: true },
        ...type === projectTimelineTypes.PHOTOS && { withImage: true }
      })
    }, 

    loadMore() {
      if (!this.loading) {
        const metaNextDates = Object.values(this.meta).map(x => x.next_at)
        const next = new Date(Math.max(...metaNextDates.map(x => new Date(x).getTime())))
  
        this.from = subWeeks(startOfDay(next), 1)
        this.to = endOfDay(next)
  
        this.init()
      }
    },

    getDate(x) {
      return ({
        [projectTimelineTypes.DEFECTS_AND_VIOLATIONS_FINISHED]: x.data?.finished_at,
        [projectTimelineTypes.APPROVAL_PROCESS_APPROVED]: x.data?.approved_at
      })[x._type] || x.created_at
    },

    prepareDate(x) {
      return labelAt(x, { withCapital: true, withTime: false, withAlias: true, zoned: false, iso: true })
    },

    showAll({ key, date, type }) {
      (({
        [projectTimelineTypes.DOCUMENTS]: () => this.$router.push({
          name: 'project.documents',
          query: {
            view: treeTypes.TREE,
            from: date,
            to: date  
          }  
        })
      })[type] || (() => {
        this.scrollTop = this.$refs.timeline.scrollTop

        this.dispalyedEvents = [{
          key,
          date,
          [type]: this.timeline.find(x => x.key === key)[type]
        }]
      }))()
    },

    goBack() {
      this.dispalyedEvents = []

      setTimeout(() => this.$refs.timeline.scrollTo(0, this.scrollTop))
    }
  }
}
</script>